import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { InternalBackButton } from '@/components/InternalPageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// axios already has withCredentials:true set globally in App.js

// ── Helpers ──────────────────────────────────────────────────────────────────
const AGING_COLOR = { Healthy: '#059669', Watch: '#ca8a04', Risk: '#ea580c', Expired: '#dc2626' };
const RISK_COLOR  = { High: '#dc2626', Medium: '#ea580c', Low: '#059669', Healthy: '#059669' };
const STATUS_COLOR = { Fresh: '#059669', Watch: '#ca8a04', Stale: '#dc2626' };

function normalizeCode(value) {
  return String(value || '').trim().toUpperCase();
}

function resolveProductDisplay(productCode, productMap = {}) {
  const rawCode = String(productCode || '').trim();
  if (!rawCode) return { primary: '—', secondary: '' };

  const mapped = productMap[normalizeCode(rawCode)];
  if (mapped && String(mapped).trim() && normalizeCode(mapped) !== normalizeCode(rawCode)) {
    return { primary: String(mapped).trim(), secondary: rawCode };
  }
  return { primary: rawCode, secondary: '' };
}

function inlineProductLabel(productCode, productMap = {}) {
  const disp = resolveProductDisplay(productCode, productMap);
  return disp.secondary ? `${disp.primary} (${disp.secondary})` : disp.primary;
}

function qty(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function computeInventoryTotals(categoryTrend = []) {
  return (categoryTrend || []).reduce((acc, category) => {
    acc.totalStock += qty(category.total_available);
    acc.totalReserved += qty(category.total_reserved);
    acc.totalAfterRes += qty(category.total_after_res);
    acc.productCount += qty(category.product_count);
    return acc;
  }, { totalStock: 0, totalReserved: 0, totalAfterRes: 0, productCount: 0 });
}

function isOverAllocated(item) {
  return qty(item?.reserved_qty) > qty(item?.available_qty);
}

function formatPressureDisplay(item) {
  const pct = qty(item?.reservation_pressure_pct);
  const avail = qty(item?.available_qty);
  if (avail <= 0) return `CRITICAL — No Stock`;
  if (pct > 1000) return `CRITICAL ${pct.toFixed(0)}%`;
  return `${pct.toFixed(0)}%`;
}

function getPressureLabel(pct, availableQty) {
  if (availableQty <= 0) return { text: "CRITICAL — No Stock", color: "#dc2626" };
  if (pct > 1000) return { text: "CRITICAL", color: "#dc2626" };
  if (pct > 200) return { text: "SEVERE", color: "#ea580c" };
  return null;
}

function ProductIdentity({ productCode, productMap, testId }) {
  const disp = resolveProductDisplay(productCode, productMap);
  return (
    <div data-testid={testId}>
      <p className="font-medium text-[#09090b] leading-tight">{disp.primary}</p>
      {disp.secondary && (
        <p className="text-[10px] text-[#9CA3AF] mt-0.5">{disp.secondary}</p>
      )}
    </div>
  );
}

function EmptyCard({ message = "No analytics available for this cycle" }) {
  return (
    <div className="flex items-center justify-center h-20 text-xs text-[#9CA3AF] italic">
      {message}
    </div>
  );
}

function SectionTitle({ children, description, testId, className = 'mb-3' }) {
  return (
    <div className={className} data-testid={testId}>
      <h2 className="text-base font-bold text-[#09090b] tracking-tight">{children}</h2>
      {description && <p className="text-xs text-[#6B7280] mt-1">{description}</p>}
    </div>
  );
}

function Card({ title, subtitle, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[#E5E7EB] rounded-xl shadow-sm p-4"
    >
      {title && (
        <div className="mb-3">
          <p className="text-sm font-semibold text-[#09090b]">{title}</p>
          {subtitle && <p className="text-xs text-[#6B7280] mt-0.5">{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

function ExplainButton({ signals, label = "Explain Why" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(v => !v)}
        className="text-xs text-[#6366f1] hover:underline"
        data-testid="explain-insight-btn"
      >
        {open ? 'Hide' : label}
      </button>
      {open && (
        <div className="mt-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-2 text-xs text-[#374151] space-y-0.5">
          {signals.map((s, i) => <div key={i}>• {s}</div>)}
        </div>
      )}
    </div>
  );
}

function AgingBadge({ cls }) {
  const color = AGING_COLOR[cls] || '#9CA3AF';
  return (
    <span style={{ background: color + '18', color, border: `1px solid ${color}40` }}
      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full">{cls}</span>
  );
}

function PressureBar({ value, max = 100 }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const color = pct > 70 ? '#dc2626' : pct > 40 ? '#ca8a04' : '#059669';
  return (
    <div className="w-full bg-[#F3F4F6] rounded-full h-1.5 mt-1">
      <div style={{ width: `${pct}%`, background: color }} className="h-1.5 rounded-full transition-all" />
    </div>
  );
}

// ── Section 0 — Command Center Header ────────────────────────────────────────
function CommandCenterHeader({ pipelineStatus, masterStatus }) {
  const lastExport  = masterStatus?.last_ingest;
  const lastSync    = pipelineStatus?.last_successful_sync;
  const snapshotId  = pipelineStatus?.active_snapshot?.version_id || '—';
  const fingerprint = masterStatus?.last_fingerprint || pipelineStatus?.active_snapshot?.version_id?.slice(-8) || '—';

  const fmt = ts => ts ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  const freshness = (() => {
    if (!lastSync) return { label: 'Unknown', color: '#9CA3AF' };
    const mins = (Date.now() - new Date(lastSync).getTime()) / 60000;
    if (mins < 10) return { label: 'Fresh',  color: '#16a34a' };
    if (mins < 30) return { label: 'Watch',  color: '#ca8a04' };
    return           { label: 'Stale',  color: '#dc2626' };
  })();

  const items = [
    { label: 'Last Export',      value: fmt(lastExport),   hint: 'Master export file last ingested' },
    { label: 'Last Sync',        value: fmt(lastSync),     hint: 'Live pipeline last uploaded' },
    { label: 'Snapshot ID',      value: snapshotId.slice(0, 20), hint: 'Active inventory snapshot' },
    { label: 'Fingerprint',      value: fingerprint,       hint: 'Unique cycle fingerprint' },
  ];

  return (
    <div data-testid="command-center-header"
      className="bg-[#09090b] text-white rounded-xl p-4 mb-6 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] text-[#9CA3AF] uppercase tracking-widest mb-0.5">RM Insights</p>
          <p className="text-base sm:text-lg font-bold truncate">Intelligence Dashboard</p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-2">
          {items.map(({ label, value, hint }) => (
            <div key={label} title={hint} className="min-w-0">
              <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">{label}</p>
              <p className="text-xs font-mono text-[#E5E7EB] mt-0.5 truncate">{value}</p>
            </div>
          ))}
          <div>
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Pipeline</p>
            <span style={{ color: freshness.color }}
              className="text-xs font-semibold">{freshness.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section 2 — Priority Alert Banner ────────────────────────────────────────
function AlertBanner({ alerts }) {
  if (!alerts || alerts.length === 0) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="priority-alert-banner">
      {alerts.map((a, i) => (
        <div key={i}
          style={{ background: (RISK_COLOR[a.level] || '#dc2626') + '12',
                   borderLeft: `4px solid ${RISK_COLOR[a.level] || '#dc2626'}` }}
          className="p-3 rounded-r-lg flex items-start gap-2 overflow-hidden">
          <span style={{ color: RISK_COLOR[a.level] || '#dc2626' }}
            className="text-xs font-bold uppercase shrink-0">{a.level}</span>
          <span className="text-xs text-[#374151] break-words min-w-0">{a.message}</span>
        </div>
      ))}
    </div>
  );
}

// ── Section 1 — AI Insights Summary ──────────────────────────────────────────
function AIInsightsSummary({ analytics, productMap }) {
  const insights = [];

  if (analytics?.pressure?.length > 0) {
    const top = analytics.pressure[0];
    const overAllocated = isOverAllocated(top);
    insights.push({
      text: overAllocated
        ? `${inlineProductLabel(top.product_code, productMap)} is over-allocated — reserved ${qty(top.reserved_qty).toFixed(0)} sqm against ${qty(top.available_qty).toFixed(0)} sqm available`
        : `${inlineProductLabel(top.product_code, productMap)} has high reservation pressure (${formatPressureDisplay(top)})`,
      signals: overAllocated
        ? [`Reserved: ${qty(top.reserved_qty).toFixed(0)} sqm`, `Shortfall: ${Math.max(qty(top.reserved_qty) - qty(top.available_qty), 0).toFixed(0)} sqm`, `Pressure: ${formatPressureDisplay(top)}`]
        : [`Reserved: ${qty(top.reserved_qty).toFixed(0)} sqm`, `Stock: ${qty(top.available_qty).toFixed(0)} sqm`, `Pressure: ${qty(top.reservation_pressure_pct).toFixed(0)}%`],
      level: 'High',
    });
  }
  if (analytics?.fragmentation?.length > 0) {
    const f = analytics.fragmentation.find(x => (x.fragmentation_pct || 0) > 5);
    if (f) insights.push({
      text: `${inlineProductLabel(f.product_code, productMap)} has fragmented batches (${f.batch_count} batches, ${f.fragmentation_pct?.toFixed(0)} score)`,
      signals: [`Batches: ${f.batch_count}`, `Fragmentation: ${f.fragmentation_pct?.toFixed(1)}`],
      level: 'Medium',
    });
  }
  if (analytics?.dormant?.length > 0) {
    const d = analytics.dormant[0];
    insights.push({
      text: `${inlineProductLabel(d.product_code, productMap)} classified as slow moving — ${qty(d.available).toFixed(0)} sqm dormant`,
      signals: [`Available: ${qty(d.available).toFixed(0)} sqm`, `Dormant flag: true`],
      level: 'Medium',
    });
  }
  if (analytics?.overstock?.length > 0) {
    const o = analytics.overstock[0];
    insights.push({
      text: `${inlineProductLabel(o.product_code, productMap)} overstock risk — ${qty(o.available).toFixed(0)} sqm on hand`,
      signals: [`Available: ${qty(o.available).toFixed(0)} sqm`, `Overstock flag: true`],
      level: 'Low',
    });
  }

  const shown = insights.slice(0, 5);

  return (
    <div className="mb-6" data-testid="ai-insights-summary">
      <SectionTitle description="The most important stock risks right now, ranked by urgency.">What Needs Attention</SectionTitle>
      {shown.length === 0 ? (
        <Card><EmptyCard /></Card>
      ) : (
        <div className="space-y-2">
          {shown.map((ins, i) => (
            <div key={i} className="bg-white border border-[#E5E7EB] rounded-xl p-3 flex flex-col gap-1">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-[#09090b]">{ins.text}</p>
                <span style={{ color: RISK_COLOR[ins.level], background: RISK_COLOR[ins.level] + '18' }}
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0">{ins.level}</span>
              </div>
              <ExplainButton signals={ins.signals} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Section 3 — Inventory Health ─────────────────────────────────────────────
function InventoryHealth({ analytics, masterReservations }) {
  const pressureItems  = analytics?.pressure  || [];
  const fragmentItems  = analytics?.fragmentation || [];
  const dormantItems   = analytics?.dormant    || [];
  const overstockItems = analytics?.overstock  || [];
  const inventoryTotals = computeInventoryTotals(analytics?.categoryTrend || []);

  const totalStock    = inventoryTotals.totalStock;
  const totalReserved = inventoryTotals.totalReserved;
  const trueSellable  = Math.max(totalStock - totalReserved, 0);
  const density       = totalStock > 0 ? (totalReserved / totalStock * 100).toFixed(1) : 0;
  const densityRisk   = density > 70 ? 'Critical' : density > 40 ? 'Watch' : 'Healthy';
  const fragScore     = fragmentItems.reduce((s, x) => s + qty(x.fragmentation_pct), 0) / Math.max(1, fragmentItems.length);
  const fragRisk      = fragScore > 8 ? 'High' : fragScore > 4 ? 'Medium' : 'Low';

  const aging = masterReservations?.analytics?.aging_summary || null;
  const expiredCount = aging?.Expired || 0;
  const deadStockCount = dormantItems.length;

  const cards = [
    {
      title: 'True Sellable Stock',
      metric: `${trueSellable.toFixed(0)} sqm`,
      status: trueSellable > totalStock * 0.5 ? 'Healthy' : 'Watch',
      desc: 'Available minus all active reservations',
      signals: [`Total stock: ${totalStock.toFixed(0)} sqm`, `Reserved: ${totalReserved.toFixed(0)} sqm`],
    },
    {
      title: 'Reservation Density',
      metric: `${density}%`,
      status: densityRisk,
      desc: 'Reserved qty as % of total stock',
      signals: [`Reserved: ${totalReserved.toFixed(0)} sqm`, `Total: ${totalStock.toFixed(0)} sqm`, `Alert threshold: 70%`],
    },
    {
      title: 'Batch Fragmentation',
      metric: fragScore.toFixed(1),
      status: fragRisk === 'High' ? 'Critical' : fragRisk === 'Medium' ? 'Watch' : 'Healthy',
      desc: `${fragmentItems.length} products with fragmented batches`,
      signals: [`Products analysed: ${fragmentItems.length}`, `Avg fragmentation score: ${fragScore.toFixed(1)}`],
    },
    {
      title: 'Dead Stock',
      metric: `${deadStockCount} products`,
      status: deadStockCount > 5 ? 'Critical' : deadStockCount > 0 ? 'Watch' : 'Healthy',
      desc: 'Slow-moving stock candidates',
      signals: [`Dormant products: ${deadStockCount}`, `Expired reservations: ${expiredCount}`],
    },
  ];

  return (
    <div className="mb-6" data-testid="inventory-health">
      <SectionTitle description="How healthy is your stock? These cards show reservation load, batch quality, and idle inventory.">Stock Health Overview</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map(c => {
          const col = STATUS_COLOR[c.status] || '#9CA3AF';
          return (
            <Card key={c.title} testId={`health-card-${c.title.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-[#09090b]">{c.title}</p>
                <span style={{ color: col, background: col + '18' }}
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full">{c.status}</span>
              </div>
              <p className="text-xl font-bold text-[#09090b] number-font">{c.metric}</p>
              <p className="text-xs text-[#6B7280] mt-1">{c.desc}</p>
              <ExplainButton signals={c.signals} />
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Section 4 — Reservation Intelligence ─────────────────────────────────────
function ReservationIntelligence({ masterReservations, onExport }) {
  if (!masterReservations || masterReservations.status === 'no_data') {
    return (
      <div className="mb-6" data-testid="reservation-intelligence">
        <div className="flex items-center justify-between gap-2 mb-3">
          <SectionTitle className="mb-0" description="Which reservations are aging and may be blocking stock too long.">Reservation Aging</SectionTitle>
          <div className="flex items-center gap-1" data-testid="reservation-insights-export-controls">
            <button
              data-testid="reservation-insights-export-csv"
              onClick={() => onExport?.('reservation-insights', 'csv')}
              className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
            >CSV</button>
            <button
              data-testid="reservation-insights-export-excel"
              onClick={() => onExport?.('reservation-insights', 'xlsx')}
              className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
            >Excel</button>
          </div>
        </div>
        <Card><EmptyCard /></Card>
      </div>
    );
  }

  const reservations = masterReservations.reservations || [];
  const aging        = masterReservations.analytics?.aging_summary || {};
  const ingestedAt   = masterReservations.ingested_at
    ? new Date(masterReservations.ingested_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div className="mb-6" data-testid="reservation-intelligence">
      <div className="flex items-center justify-between gap-2 mb-3">
        <SectionTitle className="mb-0" description="Which reservations are aging and may be blocking stock too long.">Reservation Aging</SectionTitle>
        <div className="flex items-center gap-1" data-testid="reservation-insights-export-controls">
          <button
            data-testid="reservation-insights-export-csv"
            onClick={() => onExport?.('reservation-insights', 'csv')}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
          >CSV</button>
          <button
            data-testid="reservation-insights-export-excel"
            onClick={() => onExport?.('reservation-insights', 'xlsx')}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
          >Excel</button>
        </div>
      </div>
      <div className="space-y-3">
        {/* Aging Summary */}
        <Card title="Reservation Aging Summary" subtitle={`Last updated: ${ingestedAt}`}>
          <div className="flex flex-wrap gap-3 mt-1">
            {Object.entries(aging).map(([cls, count]) => (
              <div key={cls} className="flex items-center gap-1.5">
                <div style={{ background: AGING_COLOR[cls] || '#9CA3AF' }} className="w-2.5 h-2.5 rounded-full" />
                <span className="text-xs text-[#374151]">{cls}:</span>
                <span className="text-xs font-bold text-[#09090b]">{count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Reservation Aging Table */}
        <Card title="Reservation Aging" subtitle="Active reservations ranked by how long they've been pending">
          {reservations.length === 0 ? <EmptyCard /> : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      {['Product','Batch','Qty','Days','Salesman','Status'].map(h => (
                        <th key={h} className="text-left py-1.5 pr-3 font-semibold text-[#6B7280] uppercase text-[10px] tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.slice(0, 20).map((r, i) => (
                      <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                        <td className="py-1.5 pr-3 font-medium text-[#09090b] max-w-[120px] truncate">{r.product}</td>
                        <td className="py-1.5 pr-3 text-[#6B7280]">{r.batch || '—'}</td>
                        <td className="py-1.5 pr-3 number-font">{r.qty}</td>
                        <td className="py-1.5 pr-3 number-font">{r.pend_days}</td>
                        <td className="py-1.5 pr-3 text-[#6B7280]">{r.salesman || '—'}</td>
                        <td className="py-1.5"><AgingBadge cls={r.aging_class} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {reservations.length > 20 && (
                  <p className="text-xs text-[#9CA3AF] mt-2">Showing 20 of {reservations.length} reservations</p>
                )}
              </div>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-2">
                {reservations.slice(0, 20).map((r, i) => (
                  <div key={i} className="border border-[#E5E7EB] rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-xs font-medium text-[#09090b] break-words min-w-0">{r.product}</p>
                      <AgingBadge cls={r.aging_class} />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[#6B7280]">
                      <span>{r.batch || '—'}</span>
                      <span className="number-font">{r.qty} sqm</span>
                      <span className="number-font">{r.pend_days} days</span>
                    </div>
                    {r.salesman && <p className="text-[11px] text-[#9CA3AF] mt-0.5">{r.salesman}</p>}
                  </div>
                ))}
                {reservations.length > 20 && (
                  <p className="text-xs text-[#9CA3AF] mt-2">Showing 20 of {reservations.length}</p>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// ── Section 5 — Salesman Intelligence (ADMIN ONLY) ───────────────────────────
function SalesmanIntelligence({ salesmanData }) {
  if (!salesmanData || salesmanData.status === 'no_data') {
    return (
      <div className="mb-6" data-testid="salesman-intelligence">
        <SectionTitle description="Which sales team members are holding the most reservations.">Salesman Load</SectionTitle>
        <Card><EmptyCard /></Card>
      </div>
    );
  }

  const totals = salesmanData.salesman_totals || {};
  const rows = Object.entries(totals)
    .map(([name, d]) => ({ name, ...d }))
    .sort((a, b) => b.qty - a.qty);

  return (
    <div className="mb-6" data-testid="salesman-intelligence">
      <SectionTitle description="Which sales team members are holding the most reservations.">Salesman Load — Who holds reservation load</SectionTitle>
      <Card title="Reservations per Salesman" subtitle="Admin only — shows reservation load by team member">
        {rows.length === 0 ? <EmptyCard /> : (
          <>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    {['Salesman','Reservations','Qty Reserved','Expired','Risk'].map(h => (
                      <th key={h} className="text-left py-1.5 pr-3 font-semibold text-[#6B7280] uppercase text-[10px] tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                      <td className="py-1.5 pr-3 font-medium text-[#09090b]">{r.name}</td>
                      <td className="py-1.5 pr-3 number-font">{r.count}</td>
                      <td className="py-1.5 pr-3 number-font">{r.qty?.toFixed(0)}</td>
                      <td className="py-1.5 pr-3 number-font text-[#dc2626]">{r.aging?.Expired || 0}</td>
                      <td className="py-1.5"><AgingBadge cls={r.aging?.Expired > 0 ? 'Expired' : r.aging?.Risk > 0 ? 'Risk' : 'Healthy'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="sm:hidden space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="border border-[#E5E7EB] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-[#09090b]">{r.name}</p>
                    <AgingBadge cls={r.aging?.Expired > 0 ? 'Expired' : r.aging?.Risk > 0 ? 'Risk' : 'Healthy'} />
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-[#6B7280]">
                    <span>{r.count} reservations</span>
                    <span className="number-font">{r.qty?.toFixed(0)} sqm</span>
                    {(r.aging?.Expired || 0) > 0 && <span className="text-[#dc2626] font-semibold">{r.aging.Expired} expired</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ── Section 6 — Product Risk Radar ───────────────────────────────────────────
function ProductRiskRadar({ analytics, masterReservations, productMap }) {
  const pressure = analytics?.pressure || [];
  const dormant  = analytics?.dormant  || [];
  const pressure_map = masterReservations?.analytics?.product_pressure || {};

  const products = pressure.slice(0, 10).map(p => {
    const resvPressure = pressure_map[p.product_code];
    const isDormant    = dormant.some(d => d.product_code === p.product_code);
    const pct          = p.reservation_pressure_pct || 0;
    const overAllocated = isOverAllocated(p);
    const risk = overAllocated || pct > 70 || resvPressure?.risk_level === 'Expired' ? 'High'
               : pct > 40 || isDormant ? 'Medium' : 'Low';
    const reason = overAllocated ? 'Over-allocated against remaining stock'
                 : pct > 70 ? 'High reservation pressure'
                 : isDormant ? 'Slow movement detected'
                 : resvPressure?.risk_level === 'Risk' ? 'Aging reservations'
                 : 'Healthy';
    return { product: p.product_code, risk, reason, pct, overAllocated, available_qty: qty(p.available_qty) };
  });

  return (
    <div className="mb-6" data-testid="product-risk-radar">
      <SectionTitle description="Products where reservations are too high or aging too long — check these first.">Products at Risk</SectionTitle>
      <Card>
        {products.length === 0 ? <EmptyCard /> : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    {['Product','Risk','Reason','Pressure'].map(h => (
                      <th key={h} className="text-left py-1.5 pr-3 font-semibold text-[#6B7280] uppercase text-[10px] tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                      <td className="py-1.5 pr-3">
                        <ProductIdentity
                          productCode={p.product}
                          productMap={productMap}
                          testId={`risk-radar-product-${i}`}
                        />
                      </td>
                      <td className="py-1.5 pr-3">
                        <span style={{ color: RISK_COLOR[p.risk], background: RISK_COLOR[p.risk] + '18' }}
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full">{p.risk}</span>
                      </td>
                      <td className="py-1.5 pr-3 text-[#6B7280]">{p.reason}</td>
                      <td className="py-1.5 pr-3 w-28">
                        <span className="number-font">{p.pct.toFixed(0)}%</span>
                        {p.overAllocated && <span className="text-[9px] text-[#dc2626] ml-1 font-semibold">OVER</span>}
                        {(() => { const label = getPressureLabel(p.pct, p.available_qty); return label ? <span className="text-[9px] font-bold block mt-0.5" style={{color: label.color}}>{label.text}</span> : null; })()}
                        <PressureBar value={Math.min(p.pct, 100)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-2">
              {products.map((p, i) => (
                <div key={i} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`risk-radar-mobile-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <ProductIdentity productCode={p.product} productMap={productMap} testId={`risk-radar-product-${i}`} />
                    <span style={{ color: RISK_COLOR[p.risk], background: RISK_COLOR[p.risk] + '18' }}
                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0">{p.risk}</span>
                  </div>
                  <p className="text-[11px] text-[#6B7280] mb-1.5">{p.reason}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#374151]">Pressure</span>
                    <span className="number-font text-xs font-semibold">
                      {p.pct.toFixed(0)}%
                      {p.overAllocated && <span className="text-[9px] text-[#dc2626] ml-1">OVER-ALLOCATED</span>}
                    </span>
                  </div>
                  {(() => { const label = getPressureLabel(p.pct, p.available_qty); return label ? <span className="text-[9px] font-bold mt-0.5" style={{color: label.color}}>{label.text}</span> : null; })()}
                  <PressureBar value={Math.min(p.pct, 100)} />
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ── Section 8 — Dead Stock Board ─────────────────────────────────────────────
function DeadStockBoard({ dormant, productMap, onExport }) {
  return (
    <div className="mb-6" data-testid="dead-stock-board">
      <div className="flex items-center justify-between gap-2 mb-3">
        <SectionTitle className="mb-0" description="Products that haven't moved much — may need a promotion or clearance sale.">Slow-Moving Stock</SectionTitle>
        <div className="flex items-center gap-1" data-testid="dead-stock-export-controls">
          <button
            data-testid="dead-stock-export-csv"
            onClick={() => onExport?.('dead-stock', 'csv')}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
          >CSV</button>
          <button
            data-testid="dead-stock-export-excel"
            onClick={() => onExport?.('dead-stock', 'xlsx')}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
          >Excel</button>
        </div>
      </div>
      <Card title="Slow-Moving Inventory" subtitle="Products with low movement — consider promotion or clearance">
        {!dormant || dormant.length === 0 ? <EmptyCard message="No slow-moving stock detected" /> : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E5E7EB]">
                    {['Product','Stock','Category','Status'].map(h => (
                      <th key={h} className="text-left py-1.5 pr-3 font-semibold text-[#6B7280] uppercase text-[10px] tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dormant.slice(0, 15).map((d, i) => (
                    <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                      <td className="py-1.5 pr-3">
                        <ProductIdentity
                          productCode={d.product_code}
                          productMap={productMap}
                          testId={`dead-stock-product-${i}`}
                        />
                      </td>
                      <td className="py-1.5 pr-3 number-font">{qty(d.available).toFixed(0)} sqm</td>
                      <td className="py-1.5 pr-3 text-[#6B7280]">{d.category || '—'}</td>
                      <td className="py-1.5">
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#fef3c7] text-[#b45309]">Slow Moving</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="sm:hidden space-y-2">
              {dormant.slice(0, 15).map((d, i) => (
                <div key={i} className="border border-[#E5E7EB] rounded-lg p-3" data-testid={`dead-stock-mobile-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <ProductIdentity productCode={d.product_code} productMap={productMap} testId={`dead-stock-product-${i}`} />
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-[#fef3c7] text-[#b45309] shrink-0">Slow Moving</span>
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1">
                    <span className="text-[#6B7280]">{d.category || 'Uncategorized'}</span>
                    <span className="number-font font-semibold">{qty(d.available).toFixed(0)} sqm</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ── Section 9 — Smart Recommendations ────────────────────────────────────────
function SmartRecommendations({ analytics, masterReservations, productMap }) {
  const recs = [];
  const aging = masterReservations?.analytics?.aging_summary || {};
  const fragmentation = analytics?.fragmentation || [];
  const dormant = analytics?.dormant || [];
  const pressure = analytics?.pressure || [];

  if ((aging.Expired || 0) > 0) {
    recs.push({
      rec: `Release ${aging.Expired} expired reservation(s)`,
      reason: `${aging.Expired} reservation(s) older than 30 days are blocking stock`,
      confidence: 'High',
      signals: [`Expired: ${aging.Expired}`, 'Threshold: 30 days'],
    });
  }
  if (fragmentation.length > 0) {
    const top = fragmentation[0];
    recs.push({
      rec: `Consolidate batches for ${inlineProductLabel(top.product_code, productMap)}`,
      reason: `${top.batch_count} batches detected — consolidation improves lot integrity`,
      confidence: 'Medium',
      signals: [`Batches: ${top.batch_count}`, `Fragmentation score: ${top.fragmentation_pct?.toFixed(1)}`],
    });
  }
  if (dormant.length > 0) {
    const topDormant = dormant.slice(0, 2).map(d => inlineProductLabel(d.product_code, productMap));
    recs.push({
      rec: `Run promotion for ${topDormant.join(', ')}`,
      reason: 'Slow-moving stock identified — promotion clears inventory',
      confidence: 'Medium',
      signals: [`Dead stock items: ${dormant.length}`],
    });
  }
  if (pressure.length > 0) {
    const top = pressure[0];
    if ((top.reservation_pressure_pct || 0) > 70) {
      const overAllocated = isOverAllocated(top);
      recs.push({
        rec: `Review order allocation for ${inlineProductLabel(top.product_code, productMap)}`,
        reason: overAllocated
          ? `Reservations exceed available stock — stock is over-committed`
          : `Reservation pressure at ${formatPressureDisplay(top)} — stock may be over-committed`,
        confidence: 'High',
        signals: overAllocated
          ? [`Pressure: ${formatPressureDisplay(top)}`, `Shortfall: ${Math.max(qty(top.reserved_qty) - qty(top.available_qty), 0).toFixed(0)} sqm`]
          : [`Pressure: ${qty(top.reservation_pressure_pct).toFixed(0)}%`, `Available: ${qty(top.available_qty).toFixed(0)} sqm`],
      });
    }
  }

  return (
    <div className="mb-6" data-testid="smart-recommendations">
      <SectionTitle description="Simple next steps based on what the data is showing right now.">Recommended Actions</SectionTitle>
      {recs.length === 0 ? <Card><EmptyCard message="No recommendations at this time" /></Card> : (
        <div className="space-y-2">
          {recs.map((r, i) => {
            const confColor = r.confidence === 'High' ? '#16a34a' : '#ca8a04';
            return (
              <Card key={i}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-semibold text-[#09090b]">{r.rec}</p>
                  <span style={{ color: confColor, background: confColor + '18' }}
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0">{r.confidence}</span>
                </div>
                <p className="text-xs text-[#6B7280]">{r.reason}</p>
                <ExplainButton signals={r.signals} />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Section 10 — Inventory Pressure Score ────────────────────────────────────
function InventoryPressureScore({ analytics, masterReservations }) {
  const pressure    = analytics?.pressure    || [];
  const dormant     = analytics?.dormant     || [];
  const overstock   = analytics?.overstock   || [];
  const fragmentation = analytics?.fragmentation || [];
  const inventoryTotals = computeInventoryTotals(analytics?.categoryTrend || []);
  const aging       = masterReservations?.analytics?.aging_summary || {};

  const totalStock    = inventoryTotals.totalStock;
  const totalReserved = inventoryTotals.totalReserved;
  const density       = totalStock > 0 ? totalReserved / totalStock : 0;

  const score = Math.min(100, Math.round(
    (density * 40) +
    (Math.min(dormant.length, 10) / 10 * 20) +
    (Math.min((aging.Expired || 0), 20) / 20 * 20) +
    (Math.min(fragmentation.length, 10) / 10 * 10) +
    (Math.min(overstock.length, 5) / 5 * 10)
  ));

  const scoreColor = score > 70 ? '#dc2626' : score > 40 ? '#ca8a04' : '#059669';
  const scoreLabel = score > 70 ? 'Critical' : score > 40 ? 'Watch' : 'Healthy';

  const factors = [
    { label: 'Reservation density',   value: `${(density * 100).toFixed(0)}%`,  weight: '40%' },
    { label: 'Low/dead stock',         value: `${dormant.length} products`,       weight: '20%' },
    { label: 'Expired reservations',   value: `${aging.Expired || 0}`,            weight: '20%' },
    { label: 'Batch fragmentation',    value: `${fragmentation.length} products`, weight: '10%' },
    { label: 'Overstock risk',         value: `${overstock.length} products`,     weight: '10%' },
  ];

  return (
    <div className="mb-6" data-testid="inventory-pressure-score">
      <SectionTitle description="How much stress is on the inventory — higher score means more problems to fix.">Overall Pressure Score</SectionTitle>
      <Card>
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <div className="flex flex-col items-center justify-center w-28 shrink-0">
            <div style={{ color: scoreColor }}
              className="text-5xl font-black number-font leading-none">{score}</div>
            <div className="text-xs text-[#6B7280] mt-1">/ 100</div>
            <span style={{ color: scoreColor, background: scoreColor + '18' }}
              className="text-xs font-semibold px-2 py-0.5 rounded-full mt-2">{scoreLabel}</span>
          </div>
          <div className="flex-1 space-y-2">
            {factors.map(f => (
              <div key={f.label} className="flex items-center justify-between text-xs">
                <span className="text-[#374151]">{f.label}</span>
                <div className="flex items-center gap-3">
                  <span className="number-font text-[#09090b] font-semibold">{f.value}</span>
                  <span className="text-[#9CA3AF] w-8 text-right">{f.weight}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// ── Section 7 — Operational Intelligence ─────────────────────────────────────
function OperationalIntelligence({ analytics, productMap }) {
  const volatility = analytics?.volatility || null;
  const top20      = analytics?.top20      || [];

  const ops = [];
  if (top20.length > 0) {
    const fast = top20.slice(0, 3);
    ops.push({
      title: 'Fast Moving Products',
      insight: fast.map(p => inlineProductLabel(p.product_code || p.code, productMap)).join(', '),
      reason: 'Highest stock consumption rate',
      action: 'Ensure reorder before stock falls critical',
    });
  }
  if (volatility?.products_analyzed) {
    ops.push({
      title: 'Reservation Volatility',
      insight: `${volatility.interpretation || 'Unknown'} pattern across ${volatility.products_analyzed} products`,
      reason: `Coefficient of variation ${qty(volatility.coefficient_of_variation).toFixed(1)}%`,
      action: 'Use tighter stock monitoring when volatility rises sharply',
    });
  }

  return (
    <div className="mb-6" data-testid="operational-intelligence">
      <SectionTitle description="Fast-moving products and demand patterns that need close monitoring.">Fast Movers & Trends</SectionTitle>
      {ops.length === 0 ? (
        <Card><EmptyCard /></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ops.map((o, i) => (
            <Card key={i} title={o.title}>
              <p className="text-sm font-semibold text-[#09090b] mb-1">{o.insight}</p>
              <p className="text-xs text-[#6B7280] mb-1">{o.reason}</p>
              <p className="text-xs text-[#6366f1]">→ {o.action}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MASTER UPLOAD PANEL ───────────────────────────────────────────────────────
function MasterUploadPanel({ masterStatus, onUploadSuccess }) {
  const [file, setFile]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/master/ingest`, fd);
      setResult(res.data);
      onUploadSuccess();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const enabled = masterStatus?.master_export_enabled;

  return (
    <div className="mb-6" data-testid="master-upload-panel">
      <Card title="Master Export Upload" subtitle="Upload Stock_Update_2.xlsx to enable reservation analytics">
        {!enabled ? (
          <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-lg p-3 text-xs text-[#92400E]">
            Master Export gate is <strong>OFF</strong>. To enable: add <code>MASTER_EXPORT_ENABLED=true</code> to backend/.env and restart backend.
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="file"
              accept=".xlsx"
              onChange={e => setFile(e.target.files[0])}
              data-testid="master-file-input"
              className="text-xs w-full border border-[#E5E7EB] rounded-lg p-2"
            />
            <button
              onClick={handleUpload}
              disabled={!file || loading}
              data-testid="master-upload-btn"
              className="px-4 py-2 bg-[#09090b] text-white text-xs font-semibold rounded-lg disabled:opacity-40"
            >
              {loading ? 'Uploading...' : 'Upload & Analyse'}
            </button>
            {error  && <p className="text-xs text-red-600">{error}</p>}
            {result && (
              <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-3 text-xs">
                <p className="font-semibold text-[#15803D]">Upload successful</p>
                <p>Fingerprint: <code>{result.fingerprint}</code></p>
                <p>Reservations: {result.rows?.batch_reservations} rows ingested</p>
                {result.aging_summary && (
                  <p>Aging: {Object.entries(result.aging_summary).map(([k, v]) => `${k}: ${v}`).join(' | ')}</p>
                )}
              </div>
            )}
          </div>
        )}
        {masterStatus?.last_ingest && (
          <p className="text-[10px] text-[#9CA3AF] mt-3">
            Last ingest: {new Date(masterStatus.last_ingest).toLocaleString('en-GB')} &nbsp;|&nbsp; Fingerprint: {masterStatus.last_fingerprint}
          </p>
        )}
      </Card>
    </div>
  );
}

function FeatureDirectory() {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const groups = [
    {
      title: 'Core AI Insights',
      items: [
        { label: 'Reservation Pressure', description: 'Shows products where reservations are taking up a high share of available stock.' },
        { label: 'Fragmentation Radar', description: 'Flags products split across many small batches that are harder to fulfil cleanly.' },
        { label: 'Dead Stock Board', description: 'Highlights slow-moving stock that may need promotion or cleanup.' },
        { label: 'Smart Recommendations', description: 'Suggests practical next actions based on the current AI signals.' },
      ],
    },
    {
      title: 'Operational Signals',
      items: [
        { label: 'Demand Velocity', description: 'Shows products whose stock is dropping faster than normal.' },
        { label: 'Stock Shock', description: 'Flags sudden stock drops that may need investigation.' },
        { label: 'Dispatch Readiness', description: 'Shows whether remaining stock is ready to support smooth dispatch.' },
        { label: 'Inbound Coverage Gap', description: 'Flags products that may run short before the next replenishment arrives.' },
        { label: 'Dead Stock Detection', description: 'Finds high-stock items with very little movement.' },
      ],
    },
    {
      title: 'Reliability Signals',
      items: [
        { label: 'Pipeline SLA Monitor', description: 'Shows whether the sync pipeline is keeping up with expected timing.' },
        { label: 'Heartbeat vs Ingest Watchdog', description: 'Compares bridge activity with actual ingest freshness to catch drift.' },
        { label: 'Snapshot Confidence', description: 'Shows how trustworthy the latest inventory snapshot looks.' },
      ],
    },
  ];

  return (
    <div className="mb-6" data-testid="pulse-hq-feature-directory">
      <SectionTitle description="What each insight module does — tap to learn more.">Feature Guide</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {groups.map((group, i) => (
          <Card key={i} title={group.title} testId={`feature-group-${i}`}>
            <div className="space-y-2">
              {group.items.map((item, idx) => {
                const key = `${i}-${idx}`;
                const open = !!expanded[key];
                return (
                  <div
                    key={idx}
                    className="px-3 py-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg cursor-pointer hover:bg-[#F3F4F6] transition-colors"
                    onClick={() => toggle(key)}
                    data-testid={`feature-chip-${i}-${idx}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-semibold text-[#111827]">{item.label}</p>
                      <span className="text-[10px] text-[#9CA3AF] ml-2">{open ? '▲' : '▼'}</span>
                    </div>
                    {open && (
                      <p className="text-[11px] text-[#6B7280] mt-1.5">{item.description}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function OpsSignalPanel({ opsSignals, productMap, onExport }) {
  const [expandedSignal, setExpandedSignal] = useState(null);
  const summary = opsSignals?.summary || {};
  const signals = opsSignals?.signals || {};
  const signalCards = [
    { key: 'demand_velocity_count', sigKey: 'demand_velocity', title: 'Demand Velocity', color: '#2563eb', description: 'Shows products whose stock is dropping faster than normal.' },
    { key: 'stock_shock_count', sigKey: 'stock_shock', title: 'Stock Shock', color: '#E63946', description: 'Flags sudden stock drops that may need investigation.' },
    { key: 'dispatch_blocked_count', sigKey: 'dispatch_readiness', title: 'Dispatch Blocked', color: '#ca8a04', description: 'Counts products where after-reservation stock is too tight for dispatch.' },
    { key: 'inbound_coverage_gap_count', sigKey: 'inbound_coverage_gap', title: 'Coverage Gaps', color: '#7c3aed', description: 'Shows products that may run short before replenishment arrives.' },
    { key: 'dead_stock_count', sigKey: 'dead_stock', title: 'Dead Stock', color: '#059669', description: 'Counts slow-moving products with high idle stock.' },
  ];

  const queue = opsSignals?.action_queue || [];

  return (
    <div className="mb-6" data-testid="ops-signal-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <SectionTitle className="mb-0" description="Five live checks that flag fast-moving operational risks.">Live Signal Engine</SectionTitle>
        <div className="flex items-center gap-1" data-testid="ops-signals-export-controls">
          <button
            data-testid="ops-signals-export-csv"
            onClick={() => onExport?.('ops-signals', 'csv')}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
          >CSV</button>
          <button
            data-testid="ops-signals-export-excel"
            onClick={() => onExport?.('ops-signals', 'xlsx')}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
          >Excel</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-3">
        {signalCards.map((card, idx) => {
          const items = signals[card.sigKey] || [];
          const isOpen = expandedSignal === card.sigKey;
          return (
            <div key={card.key} className="space-y-1">
              <div
                className="bg-white border border-[#E5E7EB] rounded-xl p-3 cursor-pointer hover:border-[#D1D5DB] transition-colors"
                onClick={() => setExpandedSignal(isOpen ? null : card.sigKey)}
                data-testid={`ops-signal-card-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">{card.title}</p>
                  <span className="text-[10px] text-[#9CA3AF]">{isOpen ? '▲' : '▼'}</span>
                </div>
                <p className="text-xl font-bold mt-1" style={{ color: card.color }}>
                  {summary?.[card.key] ?? 0}
                </p>
                <p className="text-[10px] text-[#9CA3AF] mt-0.5">{items.length > 0 ? 'Tap to view' : 'No alerts'}</p>
              </div>
              {isOpen && (
                <div className="bg-white border border-[#E5E7EB] rounded-xl p-3 space-y-1.5">
                  <p className="text-[10px] text-[#6B7280] mb-2">{card.description}</p>
                  {items.length === 0 ? (
                    <p className="text-[11px] text-[#9CA3AF] text-center py-2">No products flagged</p>
                  ) : items.slice(0, 8).map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-[#F3F4F6] last:border-0">
                      <span className="font-medium text-[#111827]">{it.product_code}</span>
                      <span className="text-[#6B7280]">
                        {it.after_res_qty != null ? `${it.after_res_qty} after-res` :
                         it.available_qty != null ? `${it.available_qty} ${it.unit || ''}` :
                         it.insight || it.status || ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Card title="Action Queue" subtitle="Top actions requiring immediate attention" testId="ops-action-queue-card">
        {queue.length === 0 ? (
          <EmptyCard message="No immediate actions in current signal window" />
        ) : (
          <div className="space-y-2">
            {queue.slice(0, 10).map((item, i) => (
              <div key={i} className="border border-[#E5E7EB] rounded-lg p-2" data-testid={`ops-action-queue-row-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <div className="min-w-0">
                    <ProductIdentity
                      productCode={item.product_code}
                      productMap={productMap}
                      testId={`ops-action-product-${i}`}
                    />
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${item.priority === 'Critical' ? 'bg-red-50 text-red-600' : item.priority === 'High' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid={`ops-action-priority-${i}`}>
                    {item.priority}
                  </span>
                </div>
                <p className="text-[11px] text-[#374151]">{item.signal}: {item.insight}</p>
                <p className="text-[11px] text-[#6366f1] mt-0.5">→ {item.action}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function ThresholdPanel({ thresholds, saving, onChange, onSave }) {
  if (!thresholds) return null;

  const fields = [
    ['velocity_min_drop_per_hour', 'Velocity min drop / hour'],
    ['shock_drop_pct', 'Shock drop %'],
    ['shock_min_drop_qty', 'Shock min drop qty'],
    ['dispatch_min_after_res', 'Dispatch min after-res qty'],
    ['coverage_min_days', 'Coverage minimum days'],
    ['dead_stock_min_qty', 'Dead stock minimum qty'],
  ];

  return (
    <div className="mb-6" data-testid="ops-threshold-panel">
      <Card title="Operational Threshold Controls" subtitle="Admin-configurable rules for the five approved signals">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {fields.map(([key, label], idx) => (
            <label key={key} className="space-y-1" data-testid={`threshold-field-${idx}`}>
              <p className="text-[11px] text-[#6B7280]">{label}</p>
              <input
                data-testid={`threshold-input-${key}`}
                type="number"
                step="0.1"
                value={thresholds[key] ?? ''}
                onChange={(e) => onChange(key, e.target.value)}
                className="w-full text-xs border border-[#E5E7EB] rounded-lg px-2 py-2"
              />
            </label>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <button
            data-testid="save-thresholds-button"
            onClick={onSave}
            disabled={saving}
            className="px-3 py-2 text-xs font-semibold rounded-lg bg-[#111827] text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Thresholds'}
          </button>
        </div>
      </Card>
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function MasterAIPage({ onBack, darkMode, toggleDarkMode }) {
  const [pipelineStatus,    setPipelineStatus]    = useState(null);
  const [masterStatus,      setMasterStatus]      = useState(null);
  const [masterReservations,setMasterReservations]= useState(null);
  const [salesmanData,      setSalesmanData]      = useState(null);
  const [analytics,         setAnalytics]         = useState({});
  const [productMap,        setProductMap]        = useState({});
  const [opsSignals,        setOpsSignals]        = useState(null);
  const [opsThresholds,     setOpsThresholds]     = useState(null);
  const [savingThresholds,  setSavingThresholds]  = useState(false);
  const [loading,           setLoading]           = useState(true);
  const [activeTab,         setActiveTab]         = useState('overview');

  const triggerExport = async (scope, format = 'xlsx') => {
    try {
      const res = await axios.get(`${API}/ai/export/${scope}?format=${format}`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: format === 'csv'
          ? 'text/csv'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `rm-${scope}-${ts}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[MasterAI] Export failed', e);
      window.alert('Export failed. Please try again.');
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ps, ms, mr, sd] = await Promise.all([
        axios.get(`${API}/sync/pipeline-status`).then(r => r.data).catch(() => null),
        axios.get(`${API}/master/status`).then(r => r.data).catch(() => null),
        axios.get(`${API}/master/analytics/reservations`).then(r => r.data).catch(() => null),
        axios.get(`${API}/master/analytics/salesman`).then(r => r.data).catch(() => null),
      ]);

      const dashboard = await axios.get(`${API}/ai/dashboard`).then(r => r.data).catch(() => null);
      setPipelineStatus(ps);
      setMasterStatus(ms);
      setMasterReservations(mr);
      setSalesmanData(sd);
      setAnalytics({
        pressure: dashboard?.pressure || [],
        fragmentation: dashboard?.fragmentation || [],
        dormant: dashboard?.dormant || [],
        overstock: dashboard?.overstock || [],
        volatility: dashboard?.volatility || null,
        top20: dashboard?.top20 || [],
        categoryTrend: dashboard?.categoryTrend || [],
      });
      setProductMap(dashboard?.productMap || {});
      setOpsSignals(dashboard?.opsSignals || null);
      setOpsThresholds(dashboard?.thresholds || null);
    } catch (e) {
      console.error('[MasterAI] Fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Derive alerts
  const alerts = [];
  const aging = masterReservations?.analytics?.aging_summary || {};
  if ((aging.Expired || 0) > 0)
    alerts.push({ level: 'Critical', message: `${aging.Expired} expired reservation(s) detected — stock may be blocked.` });
  if ((aging.Risk || 0) > 5)
    alerts.push({ level: 'High', message: `${aging.Risk} reservation(s) approaching expiry (16–30 days).` });
  const topPressure = analytics.pressure?.[0];
  if (topPressure && (topPressure.reservation_pressure_pct || 0) > 70)
    alerts.push({
      level: 'High',
      message: isOverAllocated(topPressure)
        ? `Reservations exceed available stock for ${inlineProductLabel(topPressure.product_code, productMap)}.`
        : `Reservation pressure exceeds 70% for ${inlineProductLabel(topPressure.product_code, productMap)}.`,
    });

  const tabs = [
    { id: 'overview',      label: 'Overview' },
    { id: 'operations',    label: 'Ops Signals' },
    { id: 'reservations',  label: 'Reservations' },
    { id: 'upload',        label: 'Upload' },
  ];

  const updateThresholdField = (key, rawValue) => {
    setOpsThresholds(prev => ({
      ...(prev || {}),
      [key]: rawValue,
    }));
  };

  const saveThresholds = async () => {
    if (!opsThresholds) return;
    setSavingThresholds(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(opsThresholds).map(([k, v]) => [k, Number(v)])
      );
      const res = await axios.put(`${API}/ai/ops/thresholds`, { thresholds: payload });
      setOpsThresholds(res.data?.thresholds || payload);
    } catch (e) {
      console.error('[MasterAI] Failed to save thresholds', e);
    } finally {
      setSavingThresholds(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="master-ai-page">
      {/* Top bar */}
      <div className="bg-white border-b border-[#E5E7EB] px-4 py-3 sticky top-0 z-20 overflow-hidden">
        <div className="flex items-center gap-3">
          <InternalBackButton onBack={onBack} label="Admin" testId="master-ai-back-btn" className="text-[#6B7280] hover:text-[#09090b] hover:bg-[#F3F4F6]" />
          <span className="text-[#E5E7EB]">|</span>
          <p className="text-sm font-semibold text-[#09090b] shrink-0" data-testid="rm-insights-page-title">RM Insights</p>
          <div className="hidden sm:flex items-center gap-1" data-testid="rm-insights-export-controls">
            <button
              data-testid="rm-insights-export-csv"
              onClick={() => triggerExport('rm-insights', 'csv')}
              className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
            >CSV</button>
            <button
              data-testid="rm-insights-export-excel"
              onClick={() => triggerExport('rm-insights', 'xlsx')}
              className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB]"
            >Excel</button>
          </div>
          <div className="ml-auto flex gap-1 items-center overflow-x-auto">
            {toggleDarkMode && (
              <button
                data-testid="dark-mode-toggle-hq"
                onClick={toggleDarkMode}
                className="text-xs text-[#6B7280] hover:text-[#09090b] px-2 py-1.5 rounded-lg hover:bg-[#F3F4F6] transition-colors shrink-0"
                title={darkMode ? "Light Mode" : "Dark Mode"}
              >
                {darkMode ? "☀" : "☾"}
              </button>
            )}
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors shrink-0 ${
                  activeTab === t.id ? 'bg-[#09090b] text-white' : 'text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                {t.label}
              </button>
            ))}
            <button onClick={fetchAll} data-testid="refresh-btn"
              className="text-xs text-[#6B7280] hover:text-[#09090b] px-2 py-1.5 shrink-0">
              ↺
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 pb-24 sm:pb-6 page-enter">
        {loading ? (
          <div className="flex items-center justify-center h-40 text-sm text-[#9CA3AF]">
            Loading intelligence data...
          </div>
        ) : (
          <>
            {/* Sec 0 */}
            <CommandCenterHeader pipelineStatus={pipelineStatus} masterStatus={masterStatus} />

            {/* Alerts */}
            <AlertBanner alerts={alerts} />

            {activeTab === 'overview' && (
              <>
                <FeatureDirectory />
                <AIInsightsSummary analytics={analytics} productMap={productMap} />
                <InventoryHealth analytics={analytics} masterReservations={masterReservations} />
                <InventoryPressureScore analytics={analytics} masterReservations={masterReservations} />
                <ProductRiskRadar analytics={analytics} masterReservations={masterReservations} productMap={productMap} />
                <DeadStockBoard dormant={analytics.dormant} productMap={productMap} onExport={triggerExport} />
                <SmartRecommendations analytics={analytics} masterReservations={masterReservations} productMap={productMap} />
              </>
            )}

            {activeTab === 'reservations' && (
              <>
                <ReservationIntelligence masterReservations={masterReservations} onExport={triggerExport} />
                <SalesmanIntelligence salesmanData={salesmanData} />
              </>
            )}

            {activeTab === 'operations' && (
              <>
                <OpsSignalPanel opsSignals={opsSignals} productMap={productMap} onExport={triggerExport} />
                <ThresholdPanel
                  thresholds={opsThresholds}
                  saving={savingThresholds}
                  onChange={updateThresholdField}
                  onSave={saveThresholds}
                />
                <OperationalIntelligence analytics={analytics} productMap={productMap} />
              </>
            )}

            {activeTab === 'upload' && (
              <MasterUploadPanel
                masterStatus={masterStatus}
                onUploadSuccess={fetchAll}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
